import React, { useState, useCallback } from "react";
import debounce from "lodash/debounce";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Label, Spinner } from "reactstrap";
import { Select } from "antd";
import toast from "react-hot-toast";
import { MergeVendorsApi, getAllVendors } from "../../api/pagination";

const MergeVendorsModal = ({ isOpen, toggle, gettingVendors }) => {
    const [fromVendorId, setFromVendorId] = useState(null);
    const [toVendorId, setToVendorId] = useState(null);
    const [searching, setSearching] = useState(false);
    const [mergeLoading, setMergeLoading] = useState(false);
    const [searchVendors, setSearchVendors] = useState([]);
    const [allVendors, setAllVendors] = useState([]);
    const [confirmed, setConfirmed] = useState(false);

    const fetchVendorsDebounced = useCallback(
        debounce((val) => {
            getAllVendors(1, val, 20, (res) => {
                setSearching(false);
                if (res.status === 200) {
                    setSearchVendors(res.data?.data || []);
                }
            });
        }, 500),
        []
    );

    const handleVendorSearch = (val) => {
        setSearching(true);
        fetchVendorsDebounced(val);
    };

    const handleMerge = () => {
        if (!confirmed) {
            toast.error("Please confirm you have read the message before merging");
            return;
        }
        if (!fromVendorId || !toVendorId) {
            toast.error("Please select both vendors to merge");
            return;
        }
        if (fromVendorId === toVendorId) {
            toast.error("Cannot merge a vendor into itself");
            return;
        }

        setMergeLoading(true);
        MergeVendorsApi(fromVendorId, toVendorId, (res) => {
            setMergeLoading(false);
            if (res.status === 200) {
                toast.success("Vendors merged successfully!");
                gettingVendors();
                toggle();
                setConfirmed(false);
            } else {
                toast.error(res.message || "Failed to merge vendors");
            }
        });
    };

    React.useEffect(() => {
        if (isOpen) {
            setConfirmed(false);
            getAllVendors(1, "", 20, (res) => {
                if (res.status === 200) {
                    setAllVendors(res.data?.data || []);
                }
            });
        }
    }, [isOpen]);

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered size="md">
            <ModalHeader toggle={toggle} className="border-0 pb-0">
                <h5 className="fw-bold mb-0" style={{ color: "var(--primary-red)" }}>Merge Vendors</h5>
            </ModalHeader>
            <ModalBody className="py-4">
                <div
                    className="p-3 rounded-12 border border-dashed mb-3"
                    style={{
                        backgroundColor: "rgba(227, 27, 35, 0.05)",
                        borderColor: "var(--primary-red)"
                    }}
                >
                    <p className="text-muted fs-13 mb-0">
                        Consolidate another vendor's data into another account.
                        <strong> This action cannot be undone.</strong> The source vendor will be permanently deleted.
                    </p>
                    <div className="mt-3 d-flex align-items-start gap-2 pt-2 border-top border-danger border-opacity-25">
                        <input
                            type="checkbox"
                            id="confirm-merge-vendor"
                            className="mt-1 custom-checkbox"
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            checked={confirmed}
                            onChange={(e) => setConfirmed(e.target.checked)}
                        />
                        <label htmlFor="confirm-merge-vendor" className="fs-12 text-dark fw-600 cursor-pointer mb-0">
                            I understand that merging is permanent. All data from the source will be moved to the target, and the source vendor record will be deleted.
                        </label>
                    </div>
                </div>

                <Row className="g-3">
                    <Col md={12}>
                        <Label className="mb-1 fw-600 text-muted fs-12 uppercase">Source Vendor (To be removed)</Label>
                        <Select
                            showSearch
                            placeholder="Search source vendor..."
                            className="w-100 modern-antd-select"
                            filterOption={false}
                            onSearch={handleVendorSearch}
                            loading={searching}
                            notFoundContent={searching ? <Spinner size="sm" color="primary" /> : "No vendors found"}
                            onChange={(val) => setFromVendorId(val)}
                            options={(searchVendors.length > 0 ? searchVendors : allVendors)
                                ?.map(v => ({
                                    value: v._id,
                                    label: `${v.id} - ${v.billing[0]?.company || v.name}`
                                }))}
                        />
                    </Col>
                    <Col md={12}>
                        <Label className="mb-1 fw-600 text-muted fs-12 uppercase">Destination Vendor (Target)</Label>
                        <Select
                            showSearch
                            placeholder="Search destination vendor..."
                            className="w-100 modern-antd-select"
                            filterOption={false}
                            onSearch={handleVendorSearch}
                            loading={searching}
                            notFoundContent={searching ? <Spinner size="sm" color="primary" /> : "No vendors found"}
                            onChange={(val) => setToVendorId(val)}
                            options={(searchVendors.length > 0 ? searchVendors : allVendors)
                                ?.map(v => ({
                                    value: v._id,
                                    label: `${v.id} - ${v.billing[0]?.company || v.name}`
                                }))}
                        />
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter className="border-0 pt-0">
                <Button color="secondary" outline onClick={toggle} className="rounded-8 px-4">
                    Cancel
                </Button>
                <button
                    className="red-button shadow-sm px-4"
                    onClick={handleMerge}
                    disabled={!fromVendorId || !toVendorId || mergeLoading || !confirmed}
                    style={{ width: "auto", opacity: confirmed ? 1 : 0.6 }}
                >
                    {mergeLoading ? <Spinner size="sm" /> : "Merge Vendors"}
                </button>
            </ModalFooter>
        </Modal>
    );
};

export default MergeVendorsModal;
